<template>
  <div
    class="relative flex min-h-0 grow flex-col overflow-hidden"
    data-target="page-content"
  >
    <template v-if="source.isLoading">
      <EmptyStateContainer>
        <EmptyState icon="special-spin" title="Loading"></EmptyState>
      </EmptyStateContainer>
    </template>
    <template v-else-if="source.error && !source.isFetchNextPageError">
      <EmptyStateContainer>
        <EmptyState icon="no-symbol" title="Error">
          <p>{{ source.error }}</p>

          <div class="mt-4">
            <ActionButton
              text="Retry"
              icon="arrow-path"
              @click="source.refetch"
            />
          </div>
        </EmptyState>
      </EmptyStateContainer>
    </template>
    <template v-else-if="$slots.emptyState && source.data.length === 0">
      <EmptyStateContainer>
        <slot name="emptyState"></slot>
      </EmptyStateContainer>
    </template>
    <template v-else>
      <FooterTemplate.Define>
        <div
          v-if="source.isRefetching"
          class="absolute bottom-4 right-4 flex items-center gap-2 rounded-lg bg-gray-600 px-2 py-1 text-sm text-white"
        >
          <ArrowPathIcon class="mx-auto size-4 animate-spin" />
          <span>Loading</span>
        </div>

        <div v-if="source.hasNextPage" class="m-4 mb-6 shrink-0">
          <ActionButton
            text="Load more"
            :disabled="source.isFetchingNextPage"
            :pending="source.isFetchingNextPage"
            @click="source.fetchNextPage"
          />
        </div>
      </FooterTemplate.Define>

      <ListStaticScroller v-if="disableVirtualizer" :source="source">
        <template #row="{ item, index }">
          <slot name="row" :item="item" :index="index"></slot>
        </template>

        <template #footer>
          <FooterTemplate.Use />
        </template>
      </ListStaticScroller>
      <ListVirtualScroller
        v-else
        :source="source"
        :estimate-height="estimateHeight"
      >
        <template #row="{ item, index }">
          <slot name="row" :item="item" :index="index"></slot>
        </template>

        <template #footer>
          <FooterTemplate.Use />
        </template>
      </ListVirtualScroller>
    </template>
  </div>
</template>

<script setup lang="ts" generic="Model extends GenericModel">
import ActionButton from "@/design_system/Utilities/ActionButton.vue";
import ArrowPathIcon from "@/icons/heroicons/arrow-path-outline.svg";
import { createReusableTemplate } from "@/lib/composables/template";
import type { NormalizedDataSource } from "@/types/data-source";
import type { GenericModel } from "@/types/model";

import EmptyState from "./EmptyState.vue";
import EmptyStateContainer from "./EmptyStateContainer.vue";
import type { RowProps } from "./internal/list-types";
import ListStaticScroller from "./internal/ListStaticScroller.vue";
import ListVirtualScroller from "./internal/ListVirtualScroller.vue";

defineProps<{
  source: NormalizedDataSource<Model>;

  estimateHeight?: number;
  disableVirtualizer?: boolean;
}>();

defineSlots<{
  row(props: RowProps<Model>): unknown;
  emptyState?(props: {}): unknown;
}>();

const FooterTemplate = createReusableTemplate();
</script>
